import type {
  AutoHoldCurrentCallParams,
  ResumeCallParams,
  ToggleHoldParams,
} from './index.types';
import { isInboundSession, isOutboundSession } from 'Modules/softphone/guards';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { Session } from 'sip.js';
import { SessionState as SipSessionState, Web } from 'sip.js';
import { pushToGTMDataLayer } from 'Utils/analytics';

const logger = createLogger('CallManager:HoldHandler');

/**
 * HoldHandler - Manages hold/resume/swap operations for calls
 *
 * Responsibilities:
 * - Hold active calls
 * - Resume held calls
 * - Toggle hold state
 * - Swap between calls
 * - Auto-hold logic for incoming/outbound calls
 */
export class HoldHandler {
  /**
   * Auto-hold current call before accepting a new incoming call or making a new outbound call
   */
  async autoHoldCurrentCall(params: AutoHoldCurrentCallParams): Promise<void> {
    const { currentCallId, newCallId, holdCall } = params;
    const softphoneStore = useSoftphoneStore.getState();
    const currentCall = softphoneStore.activeCalls.get(currentCallId);

    // Early return: current call already on hold
    if (currentCall?.isOnHold) {
      logger.debug('Current call already on hold', {
        currentCallId,
      });
      return;
    }

    // Early return: no valid current call
    if (!currentCall) {
      logger.debug('No current call found, skipping auto-hold');
      return;
    }

    // Auto-hold the active current call
    logger.info('Auto-holding current call before new call', {
      currentCallId,
      newCallId,
    });

    try {
      await holdCall(currentCallId);
      logger.debug('Current call successfully placed on hold', {
        currentCallId,
      });
    } catch (error) {
      logger.error('Failed to auto-hold current call, proceeding anyway', {
        currentCallId,
        newCallId,
        error: (error as Error).message,
      });
    }
  }

  /**
   * Put a specific call on hold
   */
  async holdCall(callId: string): Promise<void> {
    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    if (!call) {
      logger.error('Cannot hold: call not found', { callId });
      return;
    }

    if (call.isOnHold) {
      logger.debug('Call already on hold', { callId });
      return;
    }

    const resolved = this.resolveSessionForHoldResume(callId);
    if (!resolved) {
      logger.error('Cannot hold: no usable SIP session', {
        callId,
        hasSession: !!call.session,
        sessionState: call.session?.state ?? null,
        conferenceId: call.conferenceId ?? null,
      });
      return;
    }

    const { session, isRoomSession } = resolved;

    if (
      !isRoomSession &&
      !isOutboundSession(session) &&
      !isInboundSession(session)
    ) {
      logger.error('Cannot hold: invalid session type', { callId });
      return;
    }

    try {
      // Update state BEFORE sending re-INVITE
      // For conference calls, update all participants
      if (isRoomSession && call.conferenceId) {
        const allCalls = Array.from(store.activeCalls.values());
        for (const c of allCalls) {
          if (c.conferenceId === call.conferenceId) {
            store.updateCall(c.id, { isOnHold: true, state: 'held' });
          }
        }
      } else {
        store.updateCall(callId, {
          isOnHold: true,
          state: 'held',
        });
      }

      // Use SIP.js re-INVITE with holdModifier
      await session.invite({
        sessionDescriptionHandlerModifiers: [Web.holdModifier],
      });

      // The Call channel: the re-INVITE that carries the hold is traced off the
      // socket, this row is the state change it produced.
      EventService.getInstance().logCallEvent('Call placed on hold', {
        callId,
      });

      pushToGTMDataLayer('on_call_hold', { call_id: callId });
    } catch (error) {
      logger.error('Failed to hold call', {
        callId,
        isRoomSession,
        error: (error as Error).message,
      });
      throw error;
    }
  }

  /**
   * Resolve the SIP session to use for hold/resume operations.
   *
   * For conference participant calls, the individual SIP session is terminated
   * after REFER. In that case, the conference's `roomSession` must be used
   * for re-INVITE hold/resume operations instead.
   *
   * @returns The SIP session to use, or `null` if none is available.
   */
  private resolveSessionForHoldResume(
    callId: string
  ): { session: Session; isRoomSession: boolean } | null {
    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    if (!call) {
      return null;
    }

    // If the call has a valid (non-terminated) session, use it directly
    if (call.session && call.session.state !== SipSessionState.Terminated) {
      return { session: call.session, isRoomSession: false };
    }

    // For conference participants, fall back to the roomSession
    if (call.conferenceId && store.activeConference?.roomSession) {
      const roomSession = store.activeConference.roomSession;
      if (roomSession.state !== SipSessionState.Terminated) {
        logger.info(
          'Using conference roomSession for hold/resume (participant session terminated)',
          {
            callId,
            conferenceId: call.conferenceId,
          }
        );
        return { session: roomSession, isRoomSession: true };
      }
    }

    return null;
  }

  /**
   * Resume a call that is on hold
   */
  async resumeCall(params: ResumeCallParams): Promise<void> {
    const { callId, webrtcService, audioService, waitForAnswer } = params;
    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    if (!call) {
      logger.error('Cannot resume: call not found', { callId });
      return;
    }

    if (!call.isOnHold) {
      logger.debug('Call is not on hold', { callId });
      return;
    }

    const resolved = this.resolveSessionForHoldResume(callId);
    if (!resolved) {
      logger.error('Cannot resume: no usable SIP session', { callId });
      return;
    }

    const { session, isRoomSession } = resolved;

    if (
      !isRoomSession &&
      !isOutboundSession(session) &&
      !isInboundSession(session)
    ) {
      logger.error('Cannot resume: invalid session type', { callId });
      return;
    }

    try {
      // Clear cached stream before resume to force fresh stream retrieval
      webrtcService.clearCachedStream(callId);

      // Resume call using re-INVITE with empty modifiers to restore sendrecv
      if (waitForAnswer) {
        // session.invite() resolves when the re-INVITE is sent, not when it is answered
        await new Promise<void>((resolve, reject) => {
          session
            .invite({
              sessionDescriptionHandlerModifiers: [],
              requestDelegate: {
                onAccept: () => resolve(),
                onReject: (response) =>
                  reject(
                    new Error(
                      `Resume re-INVITE rejected with ${response.message.statusCode}`
                    )
                  ),
              },
            })
            .catch(reject);
        });
      } else {
        await session.invite({
          sessionDescriptionHandlerModifiers: [],
        });
      }

      // Update state AFTER re-INVITE completes successfully
      store.updateCall(callId, {
        isOnHold: false,
        state: 'active',
      });

      // See the note on hold above: the re-INVITE is traced, this is the state.
      EventService.getInstance().logCallEvent('Call resumed', {
        callId,
      });

      // For conference calls, update all participants in the conference
      if (isRoomSession && call.conferenceId) {
        const allCalls = Array.from(store.activeCalls.values());
        for (const c of allCalls) {
          if (c.conferenceId === call.conferenceId) {
            store.updateCall(c.id, { isOnHold: false, state: 'active' });
          }
        }
      } else {
        store.updateCall(callId, {
          isOnHold: false,
          state: 'active',
        });
      }
      pushToGTMDataLayer('on_call_resume', { call_id: callId });

      // Force remote stream update after resume
      // For room sessions, use the conference roomGuid as the stream key
      const streamKey = isRoomSession ? call.conferenceId ?? callId : callId;
      const remoteStream =
        webrtcService.getRemoteStream(streamKey) ??
        webrtcService.getRemoteStream(callId);
      if (remoteStream && remoteStream.getTracks().length > 0) {
        logger.info('Setting remote stream after resume', {
          callId,
          streamKey,
          isRoomSession,
          trackCount: remoteStream.getTracks().length,
        });
        audioService.setRemoteStream(remoteStream);
      } else {
        logger.warn('No remote stream available after resume', {
          callId,
          streamKey,
          isRoomSession,
        });
      }
    } catch (error) {
      logger.error('Failed to resume call', {
        callId,
        isRoomSession,
        error: (error as Error).message,
      });
      throw error;
    }
  }

  /**
   * Toggle hold state for a call (hold if active, resume if on hold)
   */
  async toggleHold(params: ToggleHoldParams): Promise<void> {
    const { callId, webrtcService, audioService } = params;
    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    if (!call) {
      logger.error('Cannot toggle hold: call not found', { callId });
      return;
    }

    if (call.isOnHold) {
      await this.resumeCall({ callId, webrtcService, audioService });
    } else {
      await this.holdCall(callId);
    }
  }

  /**
   * Swap the current call with a held call
   */
  async swapCall(targetCallId: string): Promise<void> {
    const store = useSoftphoneStore.getState();
    const currentCallId = store.currentCallId;

    logger.info('Swapping call', {
      currentCallId,
      targetCallId,
    });

    // Validate target call exists and is on hold
    const targetCall = store.activeCalls.get(targetCallId);
    if (!targetCall) {
      logger.error('Cannot swap: target call not found', {
        targetCallId,
        availableCalls: Array.from(store.activeCalls.keys()),
      });
      return;
    }

    if (!targetCall.isOnHold) {
      logger.warn('Cannot swap: target call is not on hold', {
        targetCallId,
        targetCallState: targetCall.state,
        isOnHold: targetCall.isOnHold,
      });
      return;
    }

    logger.debug('Target call validated', {
      targetCallId,
      targetCallState: targetCall.state,
      targetCallDirection: targetCall.direction,
    });

    // Hold current call if it exists and is not already on hold
    if (currentCallId) {
      const currentCall = store.activeCalls.get(currentCallId);
      if (currentCall && !currentCall.isOnHold) {
        logger.debug('Holding current call before swap', {
          currentCallId,
          currentCallState: currentCall.state,
        });

        try {
          await this.holdCall(currentCallId);
          logger.debug('Current call successfully placed on hold', {
            currentCallId,
          });
        } catch (error) {
          logger.error('Failed to hold current call during swap', {
            currentCallId,
            targetCallId,
            error: (error as Error).message,
            stack: (error as Error).stack,
          });
          // Don't proceed with swap if we couldn't hold the current call
          return;
        }
      } else if (currentCall?.isOnHold) {
        logger.debug('Current call already on hold, skipping hold step', {
          currentCallId,
        });
      }
    } else {
      logger.debug('No current call to hold, proceeding with swap', {
        targetCallId,
      });
    }

    // Switch to target call (it remains on hold until user resumes)
    store.setCurrentCall(targetCallId);

    logger.info('Call swapped successfully', {
      previousCallId: currentCallId,
      newCurrentCallId: targetCallId,
      targetCallRemainsOnHold: true,
    });
  }
}
